import { useState } from "react";
import { X, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const TRIGGERS = [
  { id: "new_critical",     label: "Nuevo hallazgo crítico",     description: "Se dispara cuando aparece un hallazgo con severidad = crítica." },
  { id: "new_high",         label: "Nuevo hallazgo alto",         description: "Se dispara con cada nuevo hallazgo de severidad alta." },
  { id: "kev_match",        label: "Coincidencia KEV",            description: "Se dispara cuando coincide una vulnerabilidad explotada conocida (KEV)." },
  { id: "rating_drop",      label: "Caída de calificación",       description: "Se dispara cuando la calificación general cae ≥ umbral de puntos.", hasThreshold: true },
  { id: "cert_expiring_7d", label: "Certificado vence en 7 días", description: "Se dispara cuando un certificado TLS está a 7 días o menos de expirar." },
  { id: "new_asset",        label: "Nuevo activo descubierto",    description: "Se dispara cuando se añade un nuevo subdominio o dominio a la superficie." },
];

export function NewRuleModal({ clients, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("new_critical");
  const [clientId, setClientId] = useState("");
  const [threshold, setThreshold] = useState(5);
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const t = TRIGGERS.find(x => x.id === trigger);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/alert-rules", {
        name,
        trigger,
        threshold: t?.hasThreshold ? parseInt(threshold) || 5 : null,
        client_id: clientId || null,
        recipients: recipients.split(",").map(s => s.trim()).filter(Boolean),
        enabled: true,
      });
      onCreated();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to create rule");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-card border border-border p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="new-rule-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">New alert rule</div>
            <h2 className="font-display text-2xl font-medium mt-1">Send an email when...</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-new-rule"><X className="w-4 h-4" /></button>
        </div>

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Rule name</div>
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="rule-name"
                 placeholder="e.g. Critical findings — Acme"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </label>

        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Trigger</div>
          <select value={trigger} onChange={(e) => setTrigger(e.target.value)} data-testid="rule-trigger"
                  className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none">
            {TRIGGERS.map(tr => <option key={tr.id} value={tr.id}>{tr.label}</option>)}
          </select>
          {t && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
        </div>

        {t?.hasThreshold && (
          <label className="block">
            <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Threshold (points)</div>
            <input type="number" min="1" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                   data-testid="rule-threshold"
                   className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
          </label>
        )}

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Scope (client)</div>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="rule-client"
                  className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none">
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="block">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">
            Recipients (comma-separated · optional — uses tenant default if empty)
          </div>
          <input value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="rule-recipients"
                 placeholder="soc@client.com, ciso@client.com"
                 className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none" />
        </label>

        {err && <div className="border border-sev-critical/50 bg-sev-critical/10 text-sev-critical text-xs font-mono px-3 py-2">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
          <button type="submit" disabled={busy || !name}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="submit-rule">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
            Create rule
          </button>
        </div>
      </form>
    </div>
  );
}
